import { useEffect, useState } from "react";
import { ShoppingBag, Plus, Pencil, Trash2, Upload } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { productImageUrl } from "@/pages/Store";

const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n ?? 0);
const EMPTY = { name: "", description: "", price: "", payment_mode: "link", external_link: "", image_url: "", published: true };

export default function AdminStore() {
  const [products, setProducts] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/store/products").then((r) => setProducts(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || "", price: p.price ?? "", payment_mode: p.payment_mode,
      external_link: p.external_link || "", image_url: p.image_url || "", published: p.published });
    setDialogOpen(true);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await api.post("/admin/upload-image", data);
      setForm((f) => ({ ...f, image_url: `/api/event-images/${r.data.storage_path}` }));
      toast.success("Image chargée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: form.price === "" ? null : parseFloat(String(form.price).replace(",", ".")) };
    try {
      if (editing) {
        await api.patch(`/admin/store/products/${editing.id}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/admin/store/products", payload);
        toast.success("Produit ajouté à la boutique");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le produit « ${p.name} » ?`)) return;
    try {
      await api.delete(`/admin/store/products/${p.id}`);
      toast.success("Produit supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-store-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
            <ShoppingBag className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-store-title">CETF Store</h1>
            <p className="text-sm text-slate-500 mt-0.5">Goodies, t-shirts et accessoires vendus au profit de l'association.</p>
          </div>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={openCreate} data-testid="product-create-btn">
          <Plus className="h-4 w-4 mr-2" />Ajouter un produit
        </Button>
      </div>

      {products === null ? (
        <p className="text-sm text-slate-400">Chargement…</p>
      ) : products.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="products-empty">
          Aucun produit pour le moment. Ajoutez votre premier article (t-shirt, gourde, tote bag…).
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-products-grid">
          {products.map((p) => (
            <Card key={p.id} className="border-slate-200 overflow-hidden" data-testid={`admin-product-${p.id}`}>
              {p.image_url && <img src={productImageUrl(p.image_url)} alt={p.name} className="w-full aspect-[4/3] object-cover" />}
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-slate-800 text-sm">{p.name}</p>
                  <span className="font-bold text-emerald-700 text-sm shrink-0">{p.price != null ? fmt(p.price) : "Prix libre"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={p.payment_mode === "link"
                    ? "bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100"
                    : "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100"}>
                    {p.payment_mode === "link" ? "Paiement en ligne (lien)" : "Sur place"}
                  </Badge>
                  {!p.published && <Badge className="bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-100">Masqué</Badge>}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(p)} data-testid={`product-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="prod-name">Nom *</Label>
              <Input id="prod-name" required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prod-desc">Description</Label>
              <Input id="prod-desc" maxLength={1000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="prod-price">Prix (€) <span className="text-slate-400 font-normal">(vide = prix libre)</span></Label>
                <Input id="prod-price" type="number" step="0.01" min="0" value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-form-price" />
              </div>
              <div className="space-y-1.5">
                <Label>Paiement</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                  <SelectTrigger data-testid="product-form-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="link">En ligne (lien externe)</SelectItem>
                    <SelectItem value="on_site">Sur place</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.payment_mode === "link" && (
              <div className="space-y-1.5">
                <Label htmlFor="prod-link">Lien de paiement (HelloAsso ou autre site) *</Label>
                <Input id="prod-link" type="url" required={form.payment_mode === "link"} maxLength={500} placeholder="https://www.helloasso.com/…"
                  value={form.external_link} onChange={(e) => setForm({ ...form, external_link: e.target.value })} data-testid="product-form-link" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Image du produit</Label>
              <label className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-xl p-4 cursor-pointer hover:bg-slate-50 transition-colors" data-testid="product-image-drop">
                <Upload className="h-5 w-5 text-slate-400 shrink-0" />
                <span className="text-sm text-slate-600">{uploading ? "Chargement…" : form.image_url ? "Image chargée (cliquez pour remplacer)" : "Cliquez pour charger une image"}</span>
                <input type="file" accept="image/*" className="hidden" data-testid="product-image-input" onChange={(e) => uploadImage(e.target.files?.[0])} />
              </label>
              {form.image_url && <img src={productImageUrl(form.image_url)} alt="" className="h-24 w-auto rounded-lg border border-slate-200" />}
            </div>
            <div className="flex items-center gap-2.5">
              <Checkbox id="prod-published" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v === true })} data-testid="product-form-published" />
              <Label htmlFor="prod-published" className="font-normal cursor-pointer">Visible dans la boutique</Label>
            </div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={saving || uploading} data-testid="product-form-submit">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
